import React from "react";
import { LEGAL_VERSION, LEGAL_UPDATED_AT } from "../legalVersion";
import ReactMarkdown from "react-markdown";
import rehypeSlug from "rehype-slug";
import rehypeAutolinkHeadings from "rehype-autolink-headings";
import mdContentRaw from "../legal/aviso-legal.md?raw";

// Normaliza MD para listas
function normalizeMd(src) {
  return src
    .replace(/\r\n/g, "\n")
    .replace(/([^\n])\n(?=(\d+\)|\d+\.)\s)/g, "$1\n\n")
    .replace(/([^\n])\n(?=(-|\*)\s)/g, "$1\n\n")
    .replace(/\n{3,}/g, "\n\n");
}
const mdContent = normalizeMd(mdContentRaw);

// lectura estimada
function readingTimeMinutes(text) {
  const words = (text || "").trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 200));
}

// Util: extrae texto plano de children
function childrenToText(children) {
  return React.Children.toArray(children).map((c) =>
    typeof c === "string" ? c : (c?.props?.children ? childrenToText(c.props.children) : "")
  ).join("");
}

export default function LegalPage() {
  const [href, setHref] = React.useState("");
  const minutes = React.useMemo(() => readingTimeMinutes(mdContent), []);

  React.useEffect(() => {
    const blob = new Blob([mdContent], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    setHref(url);
    return () => URL.revokeObjectURL(url);
  }, []);

  const ActionBar = (
    <div className="inline-flex gap-3 rounded-xl border border-slate-200 bg-white/90 backdrop-blur p-2 shadow-md">
      <button
        onClick={() => window.print()}
        className="px-3 py-1.5 rounded-lg bg-slate-900 text-white text-sm font-medium hover:opacity-90 shadow"
      >
        Imprimir
      </button>
      <a
        href={href}
        download={`Aviso_Legal_v${LEGAL_VERSION}.md`}
        className="px-3 py-1.5 rounded-lg bg-white border border-slate-300 text-sm font-medium hover:bg-slate-50 shadow-sm"
      >
        Descargar Markdown
      </a>
    </div>
  );

  return (
    <div className="relative">
      {/* Fondo suave */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -top-24 -left-24 h-[34rem] w-[34rem] rounded-full blur-3xl bg-gradient-to-tr from-indigo-200 via-sky-200 to-white opacity-70" />
        <div className="absolute -bottom-32 -right-24 h-[34rem] w-[34rem] rounded-full blur-3xl bg-gradient-to-tr from-fuchsia-200 via-rose-200 to-white opacity-70" />
      </div>

      {/* Cabecera */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-8">
        <h1 className="text-3xl sm:text-[2.5rem] font-extrabold tracking-[-0.02em] text-slate-900">
          <span className="bg-gradient-to-r from-slate-900 via-indigo-800 to-sky-700 bg-clip-text text-transparent">
            Aviso legal y Política de privacidad
          </span>
        </h1>

        <div className="mt-3 flex items-center gap-2 text-[11px] text-slate-600">
          <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/80 backdrop-blur px-3 py-1 shadow-sm">
            <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" />
            v{LEGAL_VERSION}
            <span className="text-slate-400">·</span>
            <span>{new Date(LEGAL_UPDATED_AT).toLocaleDateString()}</span>
            <span className="text-slate-400">·</span>
            <span>{minutes} min</span>
          </div>
        </div>

        {/* Acciones sticky (desktop) */}
        <div className="hidden md:block sticky top-16 z-10 mt-6">{ActionBar}</div>
      </div>

      {/* Contenido */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 pb-28 md:pb-10">
        <article
          className={[
            "legal-doc",
            "prose prose-slate md:prose-lg dark:prose-invert",
            // títulos
            "prose-headings:font-semibold",
            "prose-h2:mt-16 prose-h2:mb-4 prose-h3:mt-8 prose-h3:mb-2",
            // enlaces
            "prose-a:text-indigo-600 hover:prose-a:text-indigo-700",
            // listas y espaciados
            "prose-ol:pl-7 prose-ul:pl-7",
            "prose-li:my-2",
            "leading-relaxed tracking-[0.005em]",
            // caja
            "bg-white/90 backdrop-blur rounded-2xl shadow-xl ring-1 ring-slate-900/5",
            "px-4 sm:px-6 md:px-8 py-6 sm:py-8",
            "max-w-3xl mx-auto",
          ].join(" ")}
        >
          <ReactMarkdown
            rehypePlugins={[
              rehypeSlug,
              // enlaces de ancla SIN símbolo visible
              [rehypeAutolinkHeadings, { behavior: "wrap", properties: { className: "no-anchor-icon" } }],
            ]}
            components={{
              // H1 hero dentro del doc (si existe)
              h1: ({ node, ...props }) => (
                <h1
                  className="text-xl sm:text-2xl font-bold tracking-wide text-slate-800 mb-2"
                  {...props}
                />
              ),
              // H2: quitar numeración escrita ("1. ...") y usar solo badge CSS
              h2: ({ node, children, ...props }) => {
                const txt = childrenToText(children).replace(/^\s*\d+\.\s*/, "");
                return (
                  <h2 className="group scroll-mt-28 text-slate-900" {...props}>
                    {txt}
                  </h2>
                );
              },
              // H3: small-caps elegante
              h3: ({ node, children, ...props }) => {
                const txt = childrenToText(children).replace(/^\s*\d+\.\s*/, "");
                return (
                  <h3
                    className="group scroll-mt-28 text-slate-700 tracking-wider uppercase text-[0.95rem] font-semibold"
                    {...props}
                  >
                    {txt}
                  </h3>
                );
              },
              ol: ({ node, ...props }) => <ol className="list-decimal pl-7 [text-indent:0]" {...props} />,
              ul: ({ node, ...props }) => <ul className="list-disc pl-7 [text-indent:0]" {...props} />,
              li: ({ node, ...props }) => <li className="mb-2 leading-relaxed" {...props} />,
              p: ({ node, ...props }) => <p className="my-3" {...props} />,
              strong: ({ node, ...props }) => <strong className="font-semibold text-slate-900" {...props} />,
            }}
          >
            {mdContent}
          </ReactMarkdown>
        </article>
      </div>

      {/* Acciones móvil */}
      <div className="md:hidden fixed inset-x-0 bottom-0 z-20">
        <div
          className="mx-auto max-w-5xl px-3 pb-3"
          style={{ paddingBottom: "calc(env(safe-area-inset-bottom,0px) + 12px)" }}
        >
          {ActionBar}
        </div>
      </div>

      {/* Estilos extra */}
      <style>{`
        /* Numeración automática + badge en H2 */
        .legal-doc { counter-reset: section; }
        .legal-doc h2 {
          position: relative;
          padding-top: .4rem;
        }
        .legal-doc h2::before {
          counter-increment: section;
          content: counter(section);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 2.1rem;
          height: 2.1rem;
          margin-right: .6rem;
          border-radius: 9999px;
          font-weight: 700;
          font-size: .9rem;
          color: #3730A3;        /* indigo-800 */
          background: #EEF2FF;   /* indigo-50 */
          box-shadow: inset 0 0 0 1px rgba(99,102,241,.25);
        }
        /* Divisor suave bajo H2 */
        .legal-doc h2::after {
          content: "";
          display: block;
          height: 1px;
          margin-top: .6rem;
          background: linear-gradient(90deg,rgba(0,0,0,0) 0%, rgba(226,232,240,1) 20%, rgba(226,232,240,1) 80%, rgba(0,0,0,0) 100%);
        }
        /* Oculta cualquier icono visible de ancla */
        .no-anchor-icon a { text-decoration: none !important; }
        .no-anchor-icon a::after { display: none !important; content: none !important; }

        @media print {
          body { background: #fff !important; }
          .prose { box-shadow: none !important; background: #fff !important; }
          .sticky, .fixed { display:none !important; }
          .legal-doc h2::before { background: #e5e7eb; color:#111827; box-shadow: none; }
          .legal-doc h2::after { background: #e5e7eb; }
        }
      `}</style>
    </div>
  );
}
